import {
  MAutocomplete,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { toExponentialUppercase } from "@app/utils/functions";
import { useEquipment, useIsotopos } from "@services/servicesGenericsTables";
import dayjs from "dayjs";
import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";

interface GammaFormProps {
  mode: string;
}

const GammaTotalForm = ({ mode }: GammaFormProps) => {
  const isView = mode === MODES_OPTIONS.VIEW;
  const { isotoposList } = useIsotopos();
  const { equipmentList, loading } = useEquipment();
  const { watch, setValue } = useFormContext();

  useEffect(() => {
    const filterData = isotoposList.find(
      (isotope) => isotope.codigo === "Cs137"
    );

    if (filterData) {
      setValue(
        "cs_gpd",
        filterData.ugammades
          ? toExponentialUppercase(parseFloat(filterData.ugammades), 3)
          : ""
      );
      setValue(
        "cs_decay_constant",
        filterData.gammades
          ? toExponentialUppercase(parseFloat(filterData.gammades), 3)
          : ""
      );
    }
  }, [isotoposList]);

  const watchAnalysis = watch("analysis");
  const watchEquipment = watch("equipment");
  const watchDateActivity = watch("date_activity");
  const watchDateCounting = watch("date_cs_source_counting");
  const watchActivityBq = watch("cs_source_activity_bq");
  const watchGpd = watch("cs_gpd");
  const watchDecayConstant = watch("cs_decay_constant");
  const watchTimeIncrement = watch("cs_time_increment_from_counting_date");

  const watchFondoCount1 = watch("cs_fondo_count_1");
  const watchFondoTime1 = watch("cs_fondo_counting_time_1");
  const watchFondoCount2 = watch("cs_fondo_count_2");
  const watchFondoTime2 = watch("cs_fondo_counting_time_2");

  const watchSourcePatternCount = watch("cs_source_pattern_count");
  const watchSourceCountingTime = watch("cs_source_counting_time");
  const watchSourceGps = watch("cs_source_gps");

  useEffect(() => {
    const equipment = watchEquipment;
    setValue("detector", equipment?.codet);
  }, [watchEquipment]);

  const filteredEquipmentList = equipmentList.filter(
    (equipment) => equipment.is_geometry
  );

  useEffect(() => {
    if (watchDateActivity && watchDateCounting) {
      // UTC conversion to get required precision
      const dateActivity = dayjs.utc(watchDateActivity);
      const dateCounting = dayjs.utc(watchDateCounting);

      if (dateActivity.isValid() && dateCounting.isValid()) {
        const timeIncrement = dateCounting.diff(dateActivity, "minute");
        setValue("cs_time_increment_from_counting_date", timeIncrement);
      }
    }
  }, [watchDateActivity, watchDateCounting]);

  useEffect(() => {
    if (
      watchActivityBq &&
      watchGpd &&
      watchDecayConstant &&
      watchTimeIncrement !== undefined
    ) {
      const activityBq = parseFloat(watchActivityBq);
      const gpd = parseFloat(watchGpd);
      const decayConstant = parseFloat(watchDecayConstant);
      const timeIncrement = parseFloat(watchTimeIncrement);

      if (
        !isNaN(activityBq) &&
        !isNaN(gpd) &&
        !isNaN(decayConstant) &&
        !isNaN(timeIncrement)
      ) {
        const gps = activityBq * gpd * Math.exp(-decayConstant * timeIncrement);
        setValue("cs_source_gps", gps.toFixed(2));
      }
    }
  }, [watchActivityBq, watchGpd, watchDecayConstant, watchTimeIncrement]);

  useEffect(() => {
    if (
      watchFondoCount1 &&
      watchFondoTime1 &&
      watchFondoCount2 &&
      watchFondoTime2
    ) {
      const fondo1 = parseFloat(watchFondoCount1) / parseFloat(watchFondoTime1);
      const fondo2 = parseFloat(watchFondoCount2) / parseFloat(watchFondoTime2);

      if (!isNaN(fondo1) && !isNaN(fondo2)) {
        const avgFondo = (fondo1 + fondo2) / 2;
        setValue(
          "avg_integral_fondo",
          avgFondo ? toExponentialUppercase(parseFloat(`${avgFondo}`), 3) : ""
        );
      }
    }
  }, [
    watchFondoCount1,
    watchFondoTime1,
    watchFondoCount2,
    watchFondoTime2,
    setValue,
  ]);

  useEffect(() => {
    if (watchSourcePatternCount && watchSourceCountingTime) {
      const sourcePatternCount = parseFloat(watchSourcePatternCount);
      const sourceCountingTime = parseFloat(watchSourceCountingTime);
      const avgFondo = parseFloat(watch("avg_integral_fondo")) || 0;

      if (
        !isNaN(sourcePatternCount) &&
        !isNaN(sourceCountingTime) &&
        !isNaN(avgFondo)
      ) {
        const netArea =
          (sourcePatternCount - avgFondo * sourceCountingTime) /
          sourceCountingTime;
        setValue(
          "cs_source_pattern_net_area",
          netArea ? toExponentialUppercase(parseFloat(`${netArea}`), 3) : ""
        );
      }
    }
  }, [
    watchSourcePatternCount,
    watchSourceCountingTime,
    watch("avg_integral_fondo"),
    setValue,
  ]);

  useEffect(() => {
    const netArea = parseFloat(watch("cs_source_pattern_net_area")) || 0;
    const sourceGps = parseFloat(watchSourceGps) || 1;

    if (!isNaN(netArea) && !isNaN(sourceGps) && sourceGps !== 0) {
      const efficiency = netArea / sourceGps;
      setValue(
        "cs_source_pattern_efficiency",
        efficiency ? toExponentialUppercase(parseFloat(`${efficiency}`), 3) : ""
      );
    }
  }, [watch("cs_source_pattern_net_area"), watchSourceGps, setValue]);

  return (
    <ResponsiveFormContainer columns={4} mt={2}>
      <MTextField
        name="detector"
        label="Detector"
        placeholder="Detector"
        fullWidth
        disabled
      />

      <MTextField
        name="cs_source_activity_bq"
        label="Actividad Bq de la fuente de Cs 137 (gamma total)"
        placeholder="Actividad Bq de la fuente de Cs 137 (gamma total)"
        type="number"
        fullWidth
        disabled={isView}
      />
      <MDatePicker
        name="date_activity"
        pickerType="date"
        label="Fecha a la que está referida la actividad"
        disabled={isView}
      />

      <MTextField
        name="cs_gpd"
        label="GPD del Cs 137 (gammas por desintegración)"
        placeholder="GPD del Cs 137 (gammas por desintegración)"
        type="number"
        fullWidth
        disabled
      />

      <MTextField
        name="cs_decay_constant"
        label="Constante de desintegración del Cs 137 (min-1)"
        placeholder="Constante de desintegración del Cs 137 (min-1)"
        type="number"
        fullWidth
        disabled
      />

      <MAutocomplete
        key={JSON.stringify(watch("cs_source_geometry"))}
        name="cs_source_geometry"
        options={filteredEquipmentList}
        label="Geometría de la fuente de Cs 137"
        getOptionLabel={(option) =>
          `${option.codigo} - ${option.descripcion}` || ""
        }
        disabled={isView}
        loading={loading}
      />
      <MDatePicker
        name="date_cs_source_counting"
        pickerType="date"
        label="Fecha de contaje de la fuente de Cs 137"
        disabled={isView}
      />

      <MTextField
        name="cs_source_counting_time"
        label="Tiempo de contaje de la fuente de Cs 137 (s)"
        placeholder="Tiempo de contaje de la fuente de Cs 137 (s)"
        type="number"
        fullWidth
        disabled={isView}
      />

      <MTextField
        name="cs_time_increment_from_counting_date"
        label="Incremento del tiempo desde la fecha de contaje a la fecha de referencia de la actividad de la fuente de Cs 137 (min)"
        placeholder="Incremento del tiempo desde la fecha de contaje a la fecha de referencia de la actividad de la fuente de Cs 137 (min)"
        type="number"
        fullWidth
        disabled
      />

      <MTextField
        name="cs_source_gps"
        label="GPS de la fuente de Cs 137 (gammas por segundo)"
        placeholder="GPS de la fuente de Cs 137 (gammas por segundo)"
        type="number"
        fullWidth
        disabled
      />
      <MTextField
        name="cs_source_pattern_count"
        label="Cuentas de la fuente patrón de Cs 137"
        placeholder="Cuentas de la fuente patrón de Cs 137"
        type="number"
        fullWidth
        disabled={isView}
      />
      <MTextField
        name="cs_fondo_count_1"
        label="Cuentas del fondo integral 1 para Cs 137 a aplicar a la calibración"
        placeholder="Cuentas del fondo integral 1 para Cs 137 a aplicar a la calibración"
        type="number"
        fullWidth
        disabled={isView}
      />
      <MTextField
        name="cs_fondo_counting_time_1"
        label="Tiempo de contaje del fondo 1"
        placeholder="Tiempo de contaje del fondo 1"
        type="number"
        fullWidth
        disabled={isView}
      />
      <MTextField
        name="cs_fondo_count_2"
        label="Cuentas del fondo integral 2 para Cs 137 a aplicar a la calibración"
        placeholder="Cuentas del fondo integral 2 para Cs 137 a aplicar a la calibración"
        type="number"
        fullWidth
        disabled={isView}
      />
      <MTextField
        name="cs_fondo_counting_time_2"
        label="Tiempo de contaje del fondo 2"
        placeholder="Tiempo de contaje del fondo 2"
        type="number"
        fullWidth
        disabled={isView}
      />
      <MTextField
        name="avg_integral_fondo"
        label="Fondo integral medio (cps)"
        placeholder="Fondo integral medio (cps)"
        type="number"
        fullWidth
        disabled
      />
      <MTextField
        name="cs_source_pattern_net_area"
        label="Area neta de la fuente patrón Cs 137 (cps)"
        placeholder="Area neta de la fuente patrón Cs 137 (cps)"
        type="number"
        fullWidth
        disabled
      />
      <MTextField
        name="cs_source_pattern_efficiency"
        label="Eficiencia fuente patrón Cs 137"
        placeholder="Eficiencia fuente patrón Cs 137"
        type="number"
        fullWidth
        disabled
      />
    </ResponsiveFormContainer>
  );
};

export default GammaTotalForm;
